import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { SANDBOX_STEPS } from '../sandboxCopy';
import { SandboxStep } from '../sandboxWalkthrough';

interface SandboxProgressBarProps {
  current: SandboxStep;
  maxReached: SandboxStep;
  onNavigate: (step: SandboxStep) => void;
  /** Signing locks the bar (spec: Sign Note ends the primary walkthrough). */
  locked?: boolean;
}

/**
 * The persistent four-step indicator the spec requires on every screen:
 * Review Subjective → Select Diagnosis → Finalize OAP → Review & Sign.
 * Previously-reached steps stay clickable so prospects can move back and
 * forth without losing selections. Connectors fill as steps complete; small
 * screens collapse to circles with a single current-step caption.
 */
const SandboxProgressBar: React.FC<SandboxProgressBarProps> = ({
  current,
  maxReached,
  onNavigate,
  locked = false,
}) => (
  <nav aria-label="Sandbox progress" className="w-full">
    <ol className="flex items-center">
      {SANDBOX_STEPS.map((label, i) => {
        const step = (i + 1) as SandboxStep;
        const isCurrent = step === current;
        const isComplete = step < current || (locked && step <= current);
        const isNavigable = !locked && step <= maxReached && !isCurrent;
        const connectorFilled = step < current || locked;

        return (
          <li key={label} className="flex flex-1 last:flex-none items-center min-w-0">
            <button
              type="button"
              disabled={!isNavigable}
              onClick={() => onNavigate(step)}
              className={`group flex shrink-0 items-center gap-2.5 rounded-lg px-1.5 sm:px-2 py-1 transition-colors ${
                isNavigable
                  ? 'cursor-pointer hover:bg-primary-50/70 dark:hover:bg-dark-gray-700'
                  : 'cursor-default'
              }`}
              aria-current={isCurrent ? 'step' : undefined}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-all ${
                  isComplete
                    ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                    : isCurrent
                      ? 'border-primary-600 bg-primary-50 text-primary-700 ring-4 ring-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-900/40'
                      : 'border-gray-300 bg-white text-gray-400 dark:border-dark-gray-600 dark:bg-dark-gray-800'
                }`}
              >
                {isComplete ? <Icon icon={Icons.check} className="h-4 w-4" /> : step}
              </span>
              <span
                className={`hidden sm:block whitespace-nowrap text-sm transition-colors ${
                  isCurrent
                    ? 'font-semibold text-primary-700 dark:text-primary-300'
                    : isComplete
                      ? `font-medium text-gray-700 dark:text-gray-200 ${
                          isNavigable
                            ? 'group-hover:text-primary-700 dark:group-hover:text-primary-300'
                            : ''
                        }`
                      : 'font-medium text-gray-400 dark:text-gray-500'
                }`}
              >
                {label}
              </span>
            </button>
            {i < SANDBOX_STEPS.length - 1 && (
              <span
                aria-hidden
                className={`mx-2 sm:mx-3 h-0.5 min-w-4 flex-1 rounded-full transition-colors ${
                  connectorFilled ? 'bg-primary-500' : 'bg-gray-200 dark:bg-dark-gray-600'
                }`}
              />
            )}
          </li>
        );
      })}
    </ol>
    <p className="sm:hidden mt-2 text-center text-xs font-semibold text-primary-700 dark:text-primary-300">
      Step {current} of {SANDBOX_STEPS.length} · {SANDBOX_STEPS[current - 1]}
    </p>
  </nav>
);

export default SandboxProgressBar;
